import { Link } from "react-router-dom";
import { Badge, Disclosure, ListRow, Sheet } from "../../../design-v2";
import { ChevronRightIcon } from "./InsightsChrome";
import {
  HOW_IT_WORKS_SECTIONS,
  HOW_IT_WORKS_TITLE,
  STATE_LABELS,
  STATE_ORDER,
  STATE_TONES,
  exerciseDetailPath,
  formatOverviewRowDescription,
  formatRowDescription,
  formatRowMeta,
  formatStateCount,
} from "../domain/exercisePerformancePresentation";
import { insightsPath } from "../screens/useInsightsData";

/* Exercise Performance building blocks (blueprint §6.7). Display only: every
   state and count is the API's. */

export function StateBadge({ state }) {
  return (
    <Badge tone={STATE_TONES[state] || "neutral"} className={`lz-insights-ep-state lz-insights-ep-state--${String(state).toLowerCase()}`}>
      {STATE_LABELS[state] || state}
    </Badge>
  );
}

// One segmented bar of the four state counts: flat fills, no gradient.
export function StateDistributionBar({ stateCounts }) {
  const total = STATE_ORDER.reduce((sum, state) => sum + (stateCounts?.[state] || 0), 0);
  const label = STATE_ORDER.map((state) => `${STATE_LABELS[state]} ${formatStateCount(stateCounts?.[state])}`).join(", ");
  return (
    <div className="lz-insights-ep-bar" role="img" aria-label={`State distribution: ${label}`} data-testid="ep-distribution">
      {total > 0 ? STATE_ORDER.map((state) => (stateCounts?.[state] ? (
        <span
          key={state}
          className={`lz-insights-ep-bar__segment lz-insights-ep-bar__segment--${state.toLowerCase()}`}
          style={{ flexGrow: stateCounts[state] }}
        />
      ) : null)) : null}
    </div>
  );
}

// Four static count tiles: facts, not filters (§6.9 item 7).
export function StateTiles({ stateCounts }) {
  return (
    <dl className="lz-insights-ep-tiles" data-testid="ep-tiles">
      {STATE_ORDER.map((state) => (
        <div key={state} className={`lz-insights-ep-tile lz-insights-ep-tile--${state.toLowerCase()}`}>
          <dt className="lz-insights-ep-tile__label">{STATE_LABELS[state]}</dt>
          <dd className="lz-insights-ep-tile__value">{formatStateCount(stateCounts?.[state])}</dd>
        </div>
      ))}
    </dl>
  );
}

// Name, state badge, "Muscle · sentence", chevron → Exercise Detail.
// `description` / `meta` override the Hub defaults (Overview, §6.4).
export function ExercisePerformanceRow({ row, timeframe, description, meta, showBadge = true }) {
  return (
    <ListRow
      as={Link}
      to={insightsPath(exerciseDetailPath(row.exerciseId), timeframe)}
      className="lz-insights-ep-row"
      // The badge sits on the title line (wrapping under a long name) so the
      // copy keeps the row's width on narrow phones; it also names the state
      // for screen readers.
      title={(
        <span className="lz-insights-ep-row__title">
          <span className="lz-insights-ep-row__name">{row.name}</span>
          {showBadge && row.state ? <StateBadge state={row.state} /> : null}
        </span>
      )}
      description={description === undefined ? formatRowDescription(row) : description}
      meta={meta}
      trailing={<ChevronRightIcon />}
      data-testid={`ep-row-${row.exerciseId}`}
    />
  );
}

// A collapsible state group with its count (§6.4 item 3).
export function StateGroup({ state, rows, timeframe, defaultOpen }) {
  return (
    <Disclosure
      className="lz-insights-ep-group"
      defaultOpen={defaultOpen}
      title={(
        <span className="lz-insights-ep-group__title" data-testid={`ep-group-${state}`}>
          <span>{STATE_LABELS[state]}</span>
          <Badge tone="neutral" className="lz-insights-ep-group__count" aria-label={`${rows.length} ${rows.length === 1 ? "exercise" : "exercises"}`}>
            {rows.length}
          </Badge>
        </span>
      )}
    >
      <div className="lz-insights-ep-rows">
        {rows.map((row) => (
          <ExercisePerformanceRow
            key={row.exerciseId}
            row={row}
            timeframe={timeframe}
            description={formatOverviewRowDescription(row)}
            meta={formatRowMeta(row)}
          />
        ))}
      </div>
    </Disclosure>
  );
}

// "How exercise performance is determined": static, factual (§6.4 item 6).
export function HowItWorksSheet({ open, onClose }) {
  return (
    <Sheet open={open} onClose={onClose} title={HOW_IT_WORKS_TITLE}>
      <div className="lz-insights-ep-howto" data-testid="ep-how-it-works">
        {HOW_IT_WORKS_SECTIONS.map((section) => (
          <section key={section.title} className="lz-insights-ep-howto__section">
            <h3 className="lz-insights-ep-howto__title">{section.title}</h3>
            <p className="lz-insights-ep-howto__body">{section.body}</p>
          </section>
        ))}
      </div>
    </Sheet>
  );
}
